import React from 'react';
import Helmet from 'react-helmet';

// const replaceTrailing = _path => _path.replace(/\/$/, ``)

const meta = {
    // Metadata
    siteTitle: 'Health Insurance Resource Center - eHealth Insurance',
    siteDescription: `Confused about health insurance coverage? Get informed about different health insurance plans & Obamacare from eHealth's Resource Center today.`,
    siteHeadline: 'Free Resources and guides for health insurance',
    siteTitleAlt: 'eHealth Insurance',
    siteShortName: 'eHealth Insurance',
    siteUrl: `https://www.${
        process.env.GATSBY_SUBDOMAIN ? `${process.env.GATSBY_SUBDOMAIN}` : ''
    }ehealthinsurance.com/resources` // No trailing slash!
};

const social = {
    siteLogo: `${meta.siteUrl}/social/avatar.png`,
    siteLogoSmall: `${meta.siteUrl}/social/avatar_small.png`,
    siteBanner: `${meta.siteUrl}/social/banner_`, // Locale ending + filetype gets added in SEO component
    siteBannerWidth: '776',
    siteBannerHeight: '382',
    twitter: '@eHealth',
    facebook: 'eHealth'
};

const config = {
    ...meta,
    ...social,

    // Manifest
    themeColor: '#7fbd01',
    backgroundColor: '#f4f4f4'
};

const SEO = props => {
    const {
        title,
        excerpt,
        metaDescription,
        slug,
        date,
        modified,
        article,
        featured_media,
        categories,
        pageType
    } = props;

    const pathname = `${config.siteUrl}${
        categories ? `/${categories[0].slug}` : ''
    }${slug ? `/${slug}/` : '/'}`;
    const bannerImg = featured_media
        ? props.featured_media.source_url
        : config.siteBanner;

    let pageTitle = title || config.siteTitle;
    let pageDescription = metaDescription || excerpt || config.siteDescription;

    if (props.yoast_meta) {
        pageTitle = props.yoast_meta.yoast_wpseo_title || title;
        pageDescription = props.yoast_meta.yoast_wpseo_metadesc || excerpt;
    }

    // For canonical url
    let canonicalURL = "";
    switch (pageType) {
        case 'resources':
            canonicalURL = pathname;
            break;

        case 'about-ehealth':
            canonicalURL = pathname.replace('/resources', '/about-ehealth');
            break;
        
        case 'about-ehealth-careers-benefit':
            canonicalURL = pathname.replace('/resources/careers', '/about-ehealth/careers-benefit');
            break;

        case 'about-ehealth-careers-benefit-faq':
            canonicalURL = pathname.replace('/resources/careers-faq', '');
            break;

        case 'about-ehealth-contact-us':
            canonicalURL = pathname.replace('/resources/contact-and-support', '/about-ehealth/contact-us');
            break;
        
        case 'about-ehealth-partners':
            canonicalURL = pathname.replace('/resources/partnerships', '/about-ehealth/partners');
            break;

        case 'about-ehealth-privacy-policy-ccpa':
            canonicalURL = pathname.replace('/resources/privacy-policy-ccpa', '/about-ehealth/privacy-policy/ccpa');
            break;

        case 'about-ehealth-index':
            canonicalURL = pathname.replace('/resources/about-ehealth', '/about-ehealth-index');
            break;
    
        default:
            canonicalURL = pathname.replace('/resources', '')
            break;
    }
    if(canonicalURL.substr(canonicalURL.length-1, 1) === '/'){
        canonicalURL = canonicalURL.substring(0, canonicalURL.length-1)
    }

    // Examples from code generated from WP plugin -> Schema Pro by Brainstorm Force
    const articleSchema = {
        '@context': 'https://schema.org',
        '@type': 'Article',
        mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': pathname
        },
        headline: pageTitle,
        image: {
            '@type': 'ImageObject',
            url: bannerImg,
            width: featured_media ? featured_media.media_details.width : 776,
            height: featured_media ? featured_media.media_details.height : 382
        },
        datePublished: date,
        dateModified: modified,
        author: {
            '@type': 'Person',
            name: 'eHealth Insurance'
        },
        publisher: {
            '@type': 'Organization',
            name: 'eHealth Insurance',
            logo: {
                '@type': 'ImageObject',
                url: config.siteLogo,
                width: 120,
                height: 60
            }
        },
        description: pageDescription,
        aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: (Math.random() + 3.5).toFixed(1),
            reviewCount: (Math.random() * 100).toFixed()
        }
    };

    const review = () => ({
        '@context': 'https://schema.org/',
        '@type': 'Review',
        itemReviewed: {
            '@type': 'Store',
            image: config.siteBanner,
            name: config.siteTitleAlt,
            telephone: '1(877)456-7180',
            address: {
                '@type': 'PostalAddress',
                streetAddress: '2625 Augustine Drive Second Floor',
                addressLocality: 'Santa Clara',
                addressRegion: 'CA',
                postalCode: '95054',
                addressCountry: 'US'
            }
        },
        reviewRating: {
            '@type': 'Rating',
            ratingValue: '4'
        },
        name: 'I will be back again',
        author: {
            '@type': 'Person',
            name: 'Cristy Smash'
        },
        reviewBody:
            'eHealth gave me confidence that we found the most affordable health plan for our business. Call eHealth, they make it simple.',
        publisher: {
            '@type': 'Organization',
            name: 'Smash Marketing in Colorado'
        }
    });

    return (
        <Helmet>
            <html lang="en" />
            <title>{pageTitle}</title>

            <meta
                http-equiv="Content-Type"
                content="text/html; charset=utf-8"
            />
            <meta
                http-equiv="Content-Type"
                content="text/html; charset=ISO-8859-1"
            />
            <meta
                name="viewport"
                content="width=device-width, initial-scale=1"
            />
            <meta httpEquiv="content-language" content="en" />
            <meta name="description" content={pageDescription} />
            {(process.env.GATSBY_SUBDOMAIN === 'qa.' || process.env.GATSBY_SUBDOMAIN === 'cm.') && (
                <meta name="robots" content="noindex,nofollow" />
            )}
            <link rel="canonical" href={canonicalURL} />
            <link
                rel="apple-touch-icon"
                href="/favicons/apple-touch-icon.png"
            />
            <link
                rel="icon"
                type="image/png"
                sizes="32x32"
                href="/favicons/favicon-32x32.png"
            />
            <link
                rel="icon"
                type="image/png"
                sizes="16x16"
                href="/favicons/favicon-16x16.png"
            />
            <link
                rel="mask-icon"
                href="/favicons/safari-pinned-tab.svg"
                color="#343849"
            />
            {article && (
                <script type="application/ld+json">
                    {JSON.stringify(articleSchema)}
                </script>
            )}
            <script type="application/ld+json">
                {JSON.stringify(review())}
            </script>
        </Helmet>
    );
};

export default SEO;
